import { z } from 'zod'
import { optionalText, pageQuerySchema, quantitySchema, versionSchema } from './common.js'
import type { UserRef } from './sales.js'
import { lineSerialsSchema } from './serials.js'

export const warehouseInputSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(20)
    .regex(/^[A-Za-z0-9_-]+$/, 'Use letters, digits, dashes or underscores'),
  name: z.string().trim().min(1).max(100),
  nameAr: optionalText(100),
})
export type WarehouseInput = z.input<typeof warehouseInputSchema>
export const warehouseUpdateSchema = warehouseInputSchema.extend({ version: versionSchema })

export interface Warehouse {
  id: string
  code: string
  name: string
  nameAr: string | null
  isMain: boolean
  archived: boolean
  version: number
}

export const stockReasonSchema = z.enum([
  'OPENING',
  'PURCHASE',
  'PURCHASE_RETURN',
  'SALE',
  'SALE_RETURN',
  'TRANSFER_OUT',
  'TRANSFER_IN',
  'ADJUSTMENT',
  /** Stock issued to a project (consumption on the project, not shrinkage) and returned from it. */
  'PROJECT_ISSUE',
  'PROJECT_RETURN',
])
export type StockReason = z.infer<typeof stockReasonSchema>

export const stockLevelQuerySchema = pageQuerySchema.extend({
  warehouseId: z.uuid().optional(),
  /** Only variants at or below their reorder level. */
  belowReorder: z.stringbool().optional(),
  productId: z.uuid().optional(),
  variantId: z.uuid().optional(),
})
export type StockLevelQuery = z.input<typeof stockLevelQuerySchema>

export interface StockLevel {
  variantId: string
  productId: string
  label: string
  labelAr: string | null
  sku: string | null
  unitSymbol: string
  /** On hand in the selected warehouse, or in all warehouses. */
  quantity: string
  reorderLevel: string
  /** Weighted-average cost per unit and the value of the quantity shown. */
  averageCost: string
  value: string
}

export const stockMovementQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  variantId: z.uuid().optional(),
  warehouseId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  /** Every variant of one product. */
  productId: z.uuid().optional(),
  reason: stockReasonSchema.optional(),
})
export type StockMovementQuery = z.input<typeof stockMovementQuerySchema>

/**
 * The documents that move stock (`StockMovement.sourceType`): the web links `sourceType` + `sourceId`
 * to the document's page. Other values can appear on migrated history.
 */
export const STOCK_SOURCE_TYPES = [
  'StockAdjustment',
  'StockTransfer',
  'SalesInvoice',
  'CreditNote',
  'Purchase',
  'SupplierReturn',
  /** Projects: a material issue or return (`/projects/material-issues/:id`). */
  'ProjectMaterialIssue',
] as const

export interface StockMovement {
  id: string
  movementDate: string
  warehouse: { id: string; code: string }
  productId: string
  variantId: string
  label: string
  labelAr: string | null
  quantity: string
  unitCost: string
  value: string
  reason: StockReason
  sourceType: string
  sourceId: string
  /**
   * The source document's number (null for an unnumbered or unknown source). Like `createdBy`: Always sent by the API; optional in the type only until the web fixtures carry it.
   */
  sourceNumber?: string | null
  /** Who recorded the movement (null for system and migrated movements). */
  createdBy?: UserRef | null
  /** Running on-hand quantity for the variant in this warehouse after the movement (when filtered by both). */
  balanceAfter: string | null
}

export const stockAdjustmentKindSchema = z.enum(['OPENING', 'ADJUSTMENT', 'COUNT'])

/**
 * Why an ADJUSTMENT was made (H-6; legacy typed its reasons). Only ADJUSTMENT documents carry one;
 * OPENING and COUNT say why by their kind. Optional for API clients (older adjustments have none);
 * the editor asks for it.
 */
export const stockAdjustmentReasonSchema = z.enum([
  'DAMAGE',
  'EXPIRED',
  'THEFT_LOSS',
  'FOUND',
  'CORRECTION',
  'OTHER',
])
export type StockAdjustmentReason = z.infer<typeof stockAdjustmentReasonSchema>

export const stockAdjustmentInputSchema = z.object({
  adjustmentDate: z.iso.date(),
  warehouseId: z.uuid(),
  kind: stockAdjustmentKindSchema,
  /** ADJUSTMENT only (422 on other kinds). */
  reason: stockAdjustmentReasonSchema.nullish(),
  notes: optionalText(500),
  lines: z
    .array(
      z.object({
        variantId: z.uuid(),
        /** OPENING: quantity received (> 0); ADJUSTMENT: signed change; COUNT: quantity counted (≥ 0). */
        quantity: quantitySchema,
        /** Cost per unit for stock added; required for OPENING and for increases. */
        unitCost: z
          .string()
          .regex(/^\d{1,12}(\.\d{1,6})?$/, 'Invalid cost')
          .nullish()
          .transform((v) => v ?? null),
        /**
         * Serial-tracked products: one per unit changed — received (OPENING, increases) or removed
         * (decreases, IN_STOCK at the warehouse). COUNT: one per unit of the difference found.
         */
        serials: lineSerialsSchema,
      }),
    )
    .min(1)
    .max(500),
})
export type StockAdjustmentInput = z.input<typeof stockAdjustmentInputSchema>

export interface StockAdjustment {
  id: string
  number: string
  adjustmentDate: string
  warehouse: { id: string; code: string; name: string }
  kind: 'OPENING' | 'ADJUSTMENT' | 'COUNT'
  /**
   * ADJUSTMENT only; null on other kinds and on adjustments recorded without one. Like `createdBy` and
   * the lines' `sku` and `systemQuantity`: Always sent by the API; optional in the type only until the web fixtures carry it.
   */
  reason?: StockAdjustmentReason | null
  notes: string | null
  status: 'POSTED' | 'VOID'
  voidReason: string | null
  totalValue: string
  /** The entry posting the value difference (null when nothing changed in value, or void). */
  journalEntryId: string | null
  lines: {
    variantId: string
    label: string
    sku?: string | null
    quantity: string
    delta: string
    /**
     * COUNT: the on hand the count was compared with, i.e. the warehouse's quantity at the end of the
     * count date (counted − delta). Null for other kinds.
     */
    systemQuantity?: string | null
    unitCost: string | null
    value: string
    /** Serials received or removed (serial-tracked products). Always sent by the API. */
    serials?: string[]
  }[]
  createdBy?: UserRef | null
  createdAt: string
}

export const stockAdjustmentListQuerySchema = pageQuerySchema.extend({
  kind: stockAdjustmentKindSchema.optional(),
  reason: stockAdjustmentReasonSchema.optional(),
  status: z.enum(['POSTED', 'VOID']).optional(),
  warehouseId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
})
export type StockAdjustmentListQuery = z.input<typeof stockAdjustmentListQuerySchema>
export type StockAdjustmentListItem = Omit<StockAdjustment, 'lines'>

/**
 * `GET /stock-adjustments/summary` (inventory.read): the list's filters other than the view (`kind`,
 * `status`), paging and sort. The reason narrows the All and Adjustments counts only, as on the list.
 */
export const stockAdjustmentSummaryQuerySchema = stockAdjustmentListQuerySchema.pick({
  q: true,
  reason: true,
  warehouseId: true,
  from: true,
  to: true,
})
export type StockAdjustmentSummaryQuery = z.input<typeof stockAdjustmentSummaryQuerySchema>

/** One month of posted adjustments and counts: stock written off and stock gained, at cost. */
export interface StockAdjustmentMonth {
  /** YYYY-MM */
  month: string
  /** Σ value of the decreases, as a positive amount. */
  writeOffs: string
  /** Σ value of the increases. */
  gains: string
  /** Posted documents that wrote stock off / added stock that month. */
  writeOffCount: number
  gainCount: number
}

/**
 * Counts per view for the current filters; each `count` equals the `total` of that view's list call.
 * `months` are the last six calendar months (oldest first, this one last) of posted ADJUSTMENT and
 * COUNT documents, following the warehouse and reason filters only (opening stock is neither a gain
 * nor a write-off). As of `asOf`, today in Asia/Muscat.
 */
export interface StockAdjustmentSummary {
  asOf: string
  all: { count: number }
  opening: { count: number }
  adjustment: { count: number }
  count: { count: number }
  void: { count: number }
  months: StockAdjustmentMonth[]
}

/** What a document form needs to offer a warehouse. */
export interface WarehouseOption {
  id: string
  code: string
  name: string
  nameAr: string | null
  isMain: boolean
}

// ── stock transfers ────────────────────────────────────────────────────────────

/**
 * Goods moved between two warehouses (legacy ST-YYYY-NNNNN). Draft → Posted → Void. Posting moves
 * the goods out of one warehouse and into the other on the transfer date, at the same value, so the
 * value of stock (and the general ledger) does not change. Voiding moves them back while they are
 * still on hand at the destination.
 */
export const stockTransferStatusSchema = z.enum(['DRAFT', 'POSTED', 'VOID'])
export type StockTransferStatus = z.infer<typeof stockTransferStatusSchema>

export const stockTransferLineInputSchema = z.object({
  variantId: z.uuid(),
  /** Above zero; whole units unless the product's unit allows fractions. */
  quantity: quantitySchema.refine((v) => Number(v) > 0, 'Quantity must be above zero'),
  /** Serial-tracked products: the serials moved, each IN_STOCK at the source warehouse. */
  serials: lineSerialsSchema,
})

const stockTransferFields = z.object({
  transferDate: z.iso.date(),
  fromWarehouseId: z.uuid(),
  toWarehouseId: z.uuid(),
  notes: optionalText(2000),
  lines: z.array(stockTransferLineInputSchema).min(1, 'Add at least one line').max(500),
})
const differentWarehouses = {
  path: ['toWarehouseId'],
  message: 'Choose a different warehouse to transfer to',
}
const warehousesDiffer = (v: { fromWarehouseId: string; toWarehouseId: string }) =>
  v.fromWarehouseId !== v.toWarehouseId
export const stockTransferInputSchema = stockTransferFields.refine(warehousesDiffer, differentWarehouses)
export type StockTransferInput = z.input<typeof stockTransferInputSchema>
export const stockTransferUpdateSchema = stockTransferFields
  .extend({ version: versionSchema })
  .refine(warehousesDiffer, differentWarehouses)
export type StockTransferUpdate = z.input<typeof stockTransferUpdateSchema>

export const stockTransferListQuerySchema = pageQuerySchema.extend({
  status: stockTransferStatusSchema.optional(),
  fromWarehouseId: z.uuid().optional(),
  toWarehouseId: z.uuid().optional(),
  /** Transfers out of or into this warehouse. */
  warehouseId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
})
export type StockTransferListQuery = z.input<typeof stockTransferListQuerySchema>

/** `GET /stock-transfers/summary` (inventory.read): the list's filters other than `status`, paging and sort. */
export const stockTransferSummaryQuerySchema = stockTransferListQuerySchema.pick({
  q: true,
  fromWarehouseId: true,
  toWarehouseId: true,
  warehouseId: true,
  from: true,
  to: true,
})
export type StockTransferSummaryQuery = z.input<typeof stockTransferSummaryQuerySchema>

/** One month of posted transfers: how many and the value at cost moved (by transfer date). */
export interface StockTransferMonth {
  /** YYYY-MM */
  month: string
  count: number
  value: string
}

/**
 * Counts per status for the current filters; each `count` equals the `total` of the list call with
 * that status. `months` are the last six calendar months (oldest first, this one last) of posted
 * transfers, following the warehouse filters only. As of `asOf`, today in Asia/Muscat.
 */
export interface StockTransferSummary {
  asOf: string
  all: { count: number }
  draft: { count: number }
  posted: { count: number }
  void: { count: number }
  months: StockTransferMonth[]
}

export interface StockTransferWarehouse {
  id: string
  code: string
  name: string
  /** Always sent by the API; optional in the type only until the web fixtures carry it. */
  nameAr?: string | null
}

export interface StockTransferListItem {
  id: string
  /** ST-2026-00001 (legacy format, yearly by transfer date) — assigned when posted. */
  number: string | null
  status: StockTransferStatus
  transferDate: string
  fromWarehouse: StockTransferWarehouse
  toWarehouse: StockTransferWarehouse
  notes: string | null
  lineCount: number
  /** Value at cost of the goods moved (0 until posted). */
  totalValue: string
  voidReason: string | null
  /**
   * Who created the transfer (null for migrated or system transfers). Like `postedAt` and `voidedAt`:
   * Always sent by the API; optional in the type only until the web fixtures carry it.
   */
  createdBy?: UserRef | null
  postedAt?: string | null
  voidedAt?: string | null
  version: number
  createdAt: string
  updatedAt: string
}

/** A transfer line with what a delivery-style print needs (no prices). */
export interface StockTransferLine {
  id: string
  lineNo: number
  variantId: string
  label: string
  /** Like `barcode` and `unitSymbolAr`: Always sent by the API; optional in the type only until the web fixtures carry it. */
  labelAr?: string | null
  sku: string | null
  barcode?: string | null
  unitSymbol: string
  unitSymbolAr?: string | null
  quantity: string
  /** Value at cost moved (set when posted; null on drafts). */
  value: string | null
  /** Serial numbers (serial-tracked products; empty otherwise). Always sent by the API; optional in the type only until the web fixtures carry it. */
  serials?: string[]
  /** The product tracks serial numbers: its lines carry one per unit. Always sent by the API; optional in the type only until the web fixtures carry it. */
  trackSerials?: boolean
}

export interface StockTransfer extends StockTransferListItem {
  lines: StockTransferLine[]
}
